/**
 * Procurement transport (docs/contracts/procurement.md §§1-4). Owner: web-procurement (W1.6).
 *
 * Every call goes through the one configured generated client (`lib/api/client`: same-origin cookie, CSRF on every
 * non-GET, reachability tracking, the 401 signal) and `unwrap()`, so a failed lookup throws and is never shown as an
 * empty list (hard rule 10). There is no fixture branch here: tests mock the transport (`lib/testing/mock-api`).
 *
 * The routes below are the paths this package codes against. api-procurement / api-purchasing / api-shipments build
 * them in parallel; when they merge, regenerate the SDK (`pnpm --filter @patchbay/web api:gen`) and swap these
 * `client.get/post` calls for the generated functions. The shapes follow the contract's tables:
 *
 *   GET  /api/v1/procurement/cases/{case_id}            CaseProcurementOut (status + every order on the case)
 *   GET  /api/v1/procurement/orders/{order_id}          HardwareOrderOut (lines, PO, shipments, units)
 *   GET  /api/v1/procurement/orders?lane=&limit=        { items: ProcurementLaneRow[] }  (the Ledger lane)
 *   GET  /api/v1/procurement/lanes                      ProcurementLaneCount[]
 *   GET  /api/v1/procurement/vendors?q=                 VendorOut[]   (Books vendors, synced hourly)
 *   GET  /api/v1/procurement/products?q=                ProductListOut (Books items + the markup setting)
 *   POST /api/v1/procurement/products/propose           CardOut (create_product confirm card; creates the Books item)
 *   POST /api/v1/procurement/orders/propose             CardOut (the order card: the request gate that creates the PO)
 *   POST /api/v1/procurement/order-lines/{line_id}/receive   ReceiveOut (partial receipts; one unit per serial)
 *   POST /api/v1/purchase-orders/{po_id}/refresh        PurchaseOrderOut (on-demand Books approval poll)
 *   POST /api/v1/shipments                              ShipmentOut (manual paste of a tracking number)
 *
 * Money fields (`amount`, `cost`, `sell_price`, `unit_cost`, `unit_sell`) are absent for roles that may not see them
 * (the serializer drops them, hard rule 5): absent means withheld, never zero.
 */
import { type ApiError, unwrap } from '../../lib/api/client'
import type { CardOut } from '../../lib/api/generated'
import { client } from '../../lib/api/generated/client.gen'

export type PoStatus =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'rejected'
  | 'issued'
  | 'partially_received'
  | 'received'
  | 'billed'
  | 'cancelled'

/** The one case-level procurement state (§2): `awaiting_approval` is the PO waiting on Books' own approval. */
export type ProcurementStatus =
  | 'awaiting_approval'
  | 'ordered'
  | 'shipped'
  | 'partially_received'
  | 'staged'
  | 'received'
  | 'installed'
  | 'billed'
  | 'rejected'
  | 'cancelled'

export type SellSource = 'books_price' | 'markup' | 'override'
export type Carrier = 'ups' | 'fedex' | 'purolator' | 'canadapost' | 'other'
export type ShipmentStatus = 'label_created' | 'in_transit' | 'out_for_delivery' | 'delivered' | 'exception' | 'unknown'
export type ProcurementLane = 'awaiting_approval' | 'ordered' | 'shipped' | 'overdue' | 'received_not_installed'

export interface ShipTo {
  kind: 'office' | 'customer_site'
  /** Required for customer_site (drop-ship); the office address is a setting. */
  address?: string | null
}

export interface PurchaseOrderOut {
  id: string
  kind: 'contractor' | 'hardware'
  books_po_id?: string | null
  books_po_number?: string | null
  /** Books organization this PO was created in. Absent on older responses. */
  books_org_id?: string | null
  /** Zoho Books page. Absent until a Books record id is stored. */
  books_url?: string | null
  status: PoStatus
  submitted_at?: string | null
  approved_at?: string | null
  /** Who approved it in Books (Patchbay never approves on Books' behalf). */
  approved_by_books?: string | null
  rejected_reason?: string | null
  /** Books' own word for the PO, e.g. `deleted` when it was removed in Books. */
  books_status?: string | null
  cancelled_at?: string | null
  cancelled_reason?: string | null
  /** A Books failure Patchbay could not get past, e.g. `books_scope_missing` (token lacks purchaseorders.CREATE). */
  error_code?: string | null
  amount?: string | null
  currency?: string | null
  books_reference_number?: string | null
  desk_po_record_id?: string | null
  reconciliation_state?: 'matched' | 'mismatch' | 'unreachable' | 'not_linked' | null
  reconciliation_mismatches?: string[]
}

export interface CatalogPartHealth {
  ok: boolean
  attempted_at?: string | null
  last_ok_at?: string | null
  last_error_at?: string | null
  seen?: number | null
  error?: string | null
}

export interface CatalogHealthOut {
  vendors: CatalogPartHealth
  products: CatalogPartHealth
  last_ok_at?: string | null
  last_error_at?: string | null
}

export interface ExistingPurchaseOrderOut {
  books_po_id: string
  books_po_number: string
  reference_number?: string | null
  vendor_books_id: string
  vendor_name: string
  status: PoStatus
  amount?: string | null
  currency: string
  lines: Array<{
    position: number
    books_item_id?: string | null
    sku?: string | null
    name: string
    description?: string | null
    quantity: number
    quantity_received: number
    unit_cost?: string | null
  }>
  desk: {
    record_id?: string | null
    state: 'matched' | 'mismatch' | 'unreachable' | 'not_linked'
    vendor_name?: string | null
    amount?: string | null
    status?: string | null
    mismatches: string[]
    detail?: string | null
  }
  attached_case_id?: string | null
}

export interface ShipmentOut {
  id: string
  order_id: string
  leg_index: number
  destination: 'office' | 'customer_site'
  note?: string | null
  carrier: Carrier
  tracking_number: string
  status: ShipmentStatus
  eta?: string | null
  last_event_at?: string | null
  source: 'email' | 'api' | 'manual'
  /** The provider's public tracking page, when the API knows it. Otherwise the UI builds the carrier's own. */
  tracking_url?: string | null
  /** True when this carrier's configured poller is available or a provider has already observed the parcel. */
  tracking_automatic?: boolean
}

export interface HardwareUnitOut {
  id: string
  order_line_id: string
  model?: string | null
  serial?: string | null
  mac?: string | null
  received_at: string
  received_by?: string | null
  location: 'office' | 'site'
  installed_wo_id?: string | null
}

export interface WorkOrderChoice {
  id: string
  display_number?: string | null
  status: 'draft' | 'sent' | 'in_progress' | 'submitted' | 'returned' | 'approved'
  scheduled_start?: string | null
  checked_in_at?: string | null
  approved_at?: string | null
}

export interface HardwareOrderLineOut {
  id: string
  product_id: string
  product_name: string
  sku?: string | null
  model?: string | null
  is_serialized: boolean
  qty: number
  qty_received: number
  unit_cost?: string | null
  unit_sell?: string | null
  sell_source: SellSource
  /** #432: customer-charge state. No dollar amount. Absent until a billing line exists. */
  customer_billing?: 'prepared' | 'posted' | 'not_billable' | 'not_billable_covered' | 'void' | null
}

export interface HardwareOrderOut {
  id: string
  case_id: string
  case_number?: string | null
  vendor_id: string
  vendor_name?: string | null
  ship_to: ShipTo
  needed_by?: string | null
  status: string
  staged_at?: string | null
  received_at?: string | null
  /** The case-level state this order contributes (same vocabulary as the case chip). */
  procurement_status?: ProcurementStatus | null
  purchase_order?: PurchaseOrderOut | null
  /** `manual`: "PO to be raised in Books" by a person (Books refused the PO, or it was chosen). */
  po_source?: 'books' | 'manual'
  manual_po_number?: string | null
  /** The API's one line for a manual PO, e.g. "PO to be raised in Books: raise it there, then enter its number here". */
  po_state_text?: string | null
  lines: HardwareOrderLineOut[]
  shipments: ShipmentOut[]
  units?: HardwareUnitOut[]
  created_at?: string | null
}

export interface CaseProcurementOut {
  case_id: string
  /** null when the case has no hardware order at all (an actual empty result, not an outage). */
  status: ProcurementStatus | null
  /** The earliest open shipment ETA, for "waiting on parts (ETA …)". */
  eta?: string | null
  orders: HardwareOrderOut[]
}

export interface VendorOut {
  id: string
  books_contact_id: string
  name: string
  emails?: string[]
  phone?: string | null
  is_contractor_firm?: boolean
}

export interface ProductOut {
  id: string
  books_item_id: string
  sku?: string | null
  name: string
  model?: string | null
  vendor_part_number?: string | null
  cost?: string | null
  /** The Books item sell price; null when Books has none set (then cost + markup applies). */
  sell_price?: string | null
  is_serialized: boolean
  conduit_component_type?: string | null
}

export interface ProductListOut {
  items: ProductOut[]
  /** `hardware_default_markup_pct` (default 75). */
  markup_pct?: number | null
}

export interface ProcurementLaneCount {
  lane: ProcurementLane
  count: number
}

export interface ProcurementLaneRow {
  order_id: string
  case_id: string
  case_number?: string | null
  customer_display?: string | null
  vendor_name?: string | null
  procurement_status?: ProcurementStatus | null
  po_status?: PoStatus | null
  books_po_number?: string | null
  /** Earliest open ETA; overdue once ETA + 1 business day has passed. */
  eta?: string | null
  overdue?: boolean
  units_waiting?: number | null
  submitted_at?: string | null
  needed_by?: string | null
}

export interface OrderLineIn {
  product_id: string
  qty: number
  sell_source: SellSource
  /** Only for `override`; otherwise the API derives it from Books / the markup setting. */
  unit_sell?: string | null
}

export interface OrderProposeIn {
  /** Stable across a retry so an uncertain response cannot create a second order/card. */
  client_request_id: string
  case_id: string
  vendor_id: string
  ship_to: ShipTo
  needed_by?: string | null
  lines: OrderLineIn[]
  /** `manual` records the order with no Books call: the PO is raised by hand in Books (#100). */
  po_mode?: 'books' | 'manual'
  manual_po_number?: string | null
}

export interface ProductProposeIn {
  /** Stable across a retry so an uncertain response cannot create a second product/card. */
  client_request_id: string
  case_id: string
  name: string
  sku?: string | null
  model?: string | null
  vendor_part_number?: string | null
  cost?: string | null
  sell_price?: string | null
  is_serialized: boolean
}

export interface UnitIn {
  serial?: string | null
  model?: string | null
  mac?: string | null
}

export interface ReceiveIn {
  qty: number
  location: 'office' | 'site'
  units: UnitIn[]
}

export interface ReceiveOut {
  line: HardwareOrderLineOut
  units: HardwareUnitOut[]
}

/** The shipments package's direct response; embedded order shipments are projected with `order_id` instead. */
export interface ManualShipmentOut extends Omit<ShipmentOut, 'order_id'> {
  hardware_order_id: string
  case_id: string
}

export type Card = CardOut

interface SdkResult<T> {
  data?: T
  error?: unknown
  response?: Response
}

function get<T>(url: string, query?: Record<string, string | number | undefined>): Promise<T> {
  const q = query ? Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined && v !== '')) : undefined
  return unwrap<T>(client.get<{ 200: T }>({ url, query: q }) as Promise<SdkResult<T>>)
}

function post<T>(url: string, body: unknown): Promise<T> {
  return unwrap<T>(client.post<{ 200: T }>({ url, body, headers: { 'Content-Type': 'application/json' } }) as Promise<SdkResult<T>>)
}

function patch<T>(url: string, body: unknown): Promise<T> {
  return unwrap<T>(client.patch<{ 200: T }>({ url, body, headers: { 'Content-Type': 'application/json' } }) as Promise<SdkResult<T>>)
}

const id = encodeURIComponent

export const procurementApi = {
  caseProcurement: (caseId: string) => get<CaseProcurementOut>(`/api/v1/procurement/cases/${id(caseId)}`),
  order: (orderId: string) => get<HardwareOrderOut>(`/api/v1/procurement/orders/${id(orderId)}`),
  lanes: () => get<ProcurementLaneCount[]>('/api/v1/procurement/lanes'),
  laneRows: (lane: ProcurementLane) => get<{ items: ProcurementLaneRow[] }>('/api/v1/procurement/orders', { lane, limit: 500 }),
  vendors: (q: string) => get<VendorOut[]>('/api/v1/procurement/vendors', { q }),
  products: (q: string) => get<ProductListOut>('/api/v1/procurement/products', { q }),
  catalogHealth: () => get<CatalogHealthOut>('/api/v1/procurement/catalog-health'),
  existingPurchaseOrders: (caseId: string, q = '') =>
    get<ExistingPurchaseOrderOut[]>(`/api/v1/procurement/cases/${id(caseId)}/purchase-order-candidates`, { q }),
  proposeExistingPurchaseOrder: (caseId: string, query: string) =>
    post<Card>(`/api/v1/procurement/cases/${id(caseId)}/purchase-orders/propose`, { query }),
  proposeProduct: (body: ProductProposeIn) => post<Card>('/api/v1/procurement/products/propose', body),
  proposeOrder: (body: OrderProposeIn) => post<Card>('/api/v1/procurement/orders/propose', body),
  recordManualPo: (orderId: string, poNumber: string) =>
    post<HardwareOrderOut>(`/api/v1/procurement/orders/${id(orderId)}/manual-po`, { po_number: poNumber }),
  receive: (lineId: string, body: ReceiveIn) => post<ReceiveOut>(`/api/v1/procurement/order-lines/${id(lineId)}/receive`, body),
  openWorkOrders: (caseId: string) => {
    const query = new URLSearchParams({ case_id: caseId, limit: '200' })
    for (const status of ['draft', 'sent', 'in_progress', 'submitted', 'returned', 'approved']) query.append('status', status)
    return get<{ items: WorkOrderChoice[] }>(`/api/v1/work-orders?${query.toString()}`)
  },
  assignUnit: (unitId: string, workOrderId: string) =>
    post<HardwareUnitOut>(`/api/v1/procurement/units/${id(unitId)}/assign`, { work_order_id: workOrderId }),
  updateUnit: (unitId: string, body: { model?: string | null; serial?: string | null; mac?: string | null }) =>
    post<Card>(`/api/v1/procurement/units/${id(unitId)}/details/propose`, body),
  refreshPo: (poId: string) => post<PurchaseOrderOut>(`/api/v1/purchase-orders/${id(poId)}/refresh`, {}),
  addShipment: (body: {
    hardware_order_id: string
    case_id: string
    text: string
    carrier?: Carrier | null
    destination?: 'office' | 'customer_site' | null
    note?: string | null
  }) => post<ManualShipmentOut[]>('/api/v1/shipments/manual', body),
  editShipment: (
    shipmentId: string,
    body: {
      carrier?: Carrier | null
      tracking_number?: string | null
      destination?: 'office' | 'customer_site' | null
      note?: string | null
    },
  ) => patch<ManualShipmentOut>(`/api/v1/shipments/${id(shipmentId)}`, body),
  markDelivered: (shipmentId: string) => post<ManualShipmentOut>(`/api/v1/shipments/${id(shipmentId)}/delivered`, {}),
  confirmCard: (cardId: string) => post<Card>(`/api/v1/cards/${id(cardId)}/confirm`, { edited_payload: null }),
  dismissCard: (cardId: string, reason?: string | null) => post<Card>(`/api/v1/cards/${id(cardId)}/dismiss`, { reason: reason ?? null }),
}

export const procurementKeys = {
  all: ['procurement'] as const,
  case: (caseId: string) => ['procurement', 'case', caseId] as const,
  order: (orderId: string) => ['procurement', 'order', orderId] as const,
  lanes: () => ['procurement', 'lanes'] as const,
  lane: (lane: ProcurementLane) => ['procurement', 'lane', lane] as const,
  vendors: (q: string) => ['procurement', 'vendors', q] as const,
  products: (q: string) => ['procurement', 'products', q] as const,
  catalogHealth: () => ['procurement', 'catalog-health'] as const,
  existingPurchaseOrders: (caseId: string, q: string) => ['procurement', 'case', caseId, 'existing-pos', q] as const,
  workOrders: (caseId: string) => ['procurement', 'case', caseId, 'work-orders'] as const,
}

/** A route the API has not shipped yet (404 before api-procurement merges, 501 from a stub): the feature is off. */
export function isFeatureOff(err: unknown): boolean {
  const e = err as ApiError | undefined
  return Boolean(e && typeof e === 'object' && 'status' in e && (e.status === 404 || e.status === 501))
}
